/**
 * useIntl Hook
 *
 * Provides access to the current locale, country configuration,
 * and localized path helper from any component in the tree.
 *
 * Must be used within an IntlProvider.
 */

import { useContext } from 'react';
import type { IntlContextValue } from '@template-project/storefront-shared';
import { IntlContext } from './IntlProvider';

/**
 * Access the current internationalization context.
 *
 * Returns:
 * - `locale`: The current locale string (e.g., 'en-US', 'en-CA')
 * - `countryConfig`: Full country configuration including currency, features, etc.
 * - `localePrefix`: URL prefix for the current locale (empty for default)
 * - `localizedPath(path)`: Generate a localized URL path
 * - `changeLocale(locale)`: Navigate to a different locale
 *
 * @throws {Error} If used outside of IntlProvider
 */
export function useIntl(): IntlContextValue {
  const context = useContext(IntlContext);

  if (!context) {
    throw new Error('useIntl must be used within an IntlProvider');
  }

  return context;
}
